/**
 * Loads and parses the ledger (Xero / GL) documents a practitioner has chosen
 * for vouching (MOR-574).
 *
 * The ledger is NOT in `bankTransactionEntries` — it's a separate library
 * document. This hook reads the chosen document ids from the task, resolves
 * their signed download URLs via `listDocuments`, fetches each spreadsheet, and
 * parses it deterministically (`parseLedgerWorkbook`) into ledger rows. The
 * rows live only in the client session; the only persisted state is the chosen
 * document ids. Matching against bank transactions happens in the panel via
 * `computeVouch`.
 *
 * Deterministic parsing only handles spreadsheets (CSV / XLSX) — non-spreadsheet
 * documents are reported as `unsupported` rather than silently ignored.
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { LedgerRow } from "@cvx/jobs/summarization/agents/bankTransactions/vouchCore";
import { parseLedgerWorkbook } from "./parseLedger";

export type LedgerDocStatus =
  "parsing" | "ok" | "empty" | "unsupported" | "error";

export interface LedgerDocResult {
  documentId: string;
  fileName: string;
  status: LedgerDocStatus;
  rowCount: number;
}

const SPREADSHEET_RE = /\.(csv|xlsx|xls|xlsm)$/i;

function isSpreadsheet(fileName: string, fileType?: string): boolean {
  if (SPREADSHEET_RE.test(fileName)) return true;
  const t = (fileType ?? "").toLowerCase();
  return (
    t.includes("csv") ||
    t.includes("spreadsheet") ||
    t.includes("excel") ||
    t.includes("ms-excel")
  );
}

interface LibraryDoc {
  _id: string;
  fileName: string;
  fileType?: string;
  category?: string;
  documentType?: string;
  url?: string | null;
}

export function useVouchLedger(
  jobId: Id<"jobs">,
  taskId: Id<"summarizationTasks">,
) {
  const { data: ledgerDocIds } = useQuery(
    convexQuery(api.jobs.summarization.publicQueries.getVouchLedgerDocuments, {
      taskId,
    }),
  );

  const { data: allDocuments } = useQuery(
    convexQuery(api.jobs.gatherInformation.documents.queries.listDocuments, {
      jobId,
    }),
  );

  const docs = useMemo(
    () => (allDocuments ?? []) as LibraryDoc[],
    [allDocuments],
  );
  const selectedIds = useMemo(() => ledgerDocIds ?? [], [ledgerDocIds]);

  // Resolve the chosen documents (id → {url, fileName}) in a stable order.
  const selectedDocs = useMemo(() => {
    const byId = new Map(docs.map((d) => [d._id, d]));
    return selectedIds
      .map((id) => byId.get(id))
      .filter((d): d is LibraryDoc => Boolean(d));
  }, [selectedIds, docs]);

  // A signature that only changes when the set of (id, url) pairs changes, so
  // the fetch effect doesn't re-run on unrelated re-renders.
  const fetchSignature = useMemo(
    () => selectedDocs.map((d) => `${d._id}:${d.url ?? ""}`).join("|"),
    [selectedDocs],
  );

  const [ledgerRows, setLedgerRows] = useState<LedgerRow[]>([]);
  const [perDoc, setPerDoc] = useState<LedgerDocResult[]>([]);
  const [isParsing, setIsParsing] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (selectedDocs.length === 0) {
      setLedgerRows([]);
      setPerDoc([]);
      setIsParsing(false);
      return;
    }

    setIsParsing(true);
    (async () => {
      const allRows: LedgerRow[] = [];
      const results: LedgerDocResult[] = [];

      for (const doc of selectedDocs) {
        if (!isSpreadsheet(doc.fileName, doc.fileType)) {
          results.push({
            documentId: doc._id,
            fileName: doc.fileName,
            status: "unsupported",
            rowCount: 0,
          });
          continue;
        }
        if (!doc.url) {
          results.push({
            documentId: doc._id,
            fileName: doc.fileName,
            status: "error",
            rowCount: 0,
          });
          continue;
        }
        try {
          const res = await fetch(doc.url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const buffer = await res.arrayBuffer();
          const rows = parseLedgerWorkbook(buffer).map((r) => ({
            ...r,
            sourceDocumentId: doc._id,
          }));
          allRows.push(...rows);
          results.push({
            documentId: doc._id,
            fileName: doc.fileName,
            status: rows.length > 0 ? "ok" : "empty",
            rowCount: rows.length,
          });
        } catch {
          results.push({
            documentId: doc._id,
            fileName: doc.fileName,
            status: "error",
            rowCount: 0,
          });
        }
      }

      if (!cancelled) {
        setLedgerRows(allRows);
        setPerDoc(results);
        setIsParsing(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchSignature, selectedDocs]);

  return {
    /** True once at least one ledger document has been chosen. */
    configured: selectedIds.length > 0,
    /** True while ledger documents are being fetched + parsed. */
    isParsing,
    /** Parsed ledger rows across all chosen documents. */
    ledgerRows,
    /** Per-document parse outcome (for surfacing unsupported / empty files). */
    perDoc,
    /** The chosen ledger document ids. */
    ledgerDocIds: selectedIds,
    /** All job library documents (for the picker). */
    allDocuments: docs,
  };
}
